/**
 * <DatePicker> — date input with min/max bounds + Hijri preview
 * (M3X.UI-HYGIENE-PASS-2 PR 2).
 *
 * Uses the native `<input type="date">` as the underlying control
 * for accessibility + locale-aware date pickers across browsers.
 * Surfaces an additional Hijri preview line in AR locale (toggleable).
 *
 *   <DatePicker
 *     value={contract.end_date}
 *     onChange={setEndDate}
 *     min={contract.start_date}
 *     required
 *     showHijri
 *   />
 *
 * The DateRangePicker composite further down uses two DatePicker
 * instances with auto-min on the end side.
 */

import { useTranslation } from "react-i18next";

import { formatHijri } from "../../lib/date/hijri";
import { DateDisplay } from "../../lib/formatters";

export interface DatePickerProps {
  /** ISO date string `YYYY-MM-DD` or null when empty. */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  /** ISO lower bound (inclusive). */
  min?: string | null;
  /** ISO upper bound (inclusive). */
  max?: string | null;
  required?: boolean;
  disabled?: boolean;
  /** Optional aria-label. */
  ariaLabel?: string;
  /** When true, surface a Hijri preview line below the input. Default
   *  true in AR locale, false otherwise. */
  showHijri?: boolean;
  className?: string;
  /** When provided, the input receives this `id` for `<label for>`. */
  id?: string;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  ariaLabel,
  showHijri,
  className,
  id,
}: DatePickerProps) {
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const shouldShowHijri = showHijri ?? isAr;

  return (
    <div className={`flex flex-col gap-1 ${className ?? ""}`}>
      <input
        id={id}
        type="date"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        min={min ?? undefined}
        max={max ?? undefined}
        required={required}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-required={required}
        className="block w-full rounded-md border border-border-default bg-surface-default px-3 py-2 text-sm shadow-sm focus:border-brand focus:ring-1 focus:ring-brand disabled:bg-surface-sunken disabled:text-text-tertiary"
        dir="ltr"
      />
      {value && (
        <p className="text-xs text-text-tertiary">
          <DateDisplay date={value} format="long" />
          {shouldShowHijri && (
            <>
              {" · "}
              <span dir="ltr">{formatHijri(value)}</span>
            </>
          )}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DateRangePicker — composite for start/end date pairs
// ---------------------------------------------------------------------------

export interface DateRangePickerProps {
  startValue: string | null | undefined;
  endValue: string | null | undefined;
  onStartChange: (value: string | null) => void;
  onEndChange: (value: string | null) => void;
  /** ISO upper bound for the end date (e.g., termination_date can't
   *  exceed today + 90 days). */
  maxEnd?: string;
  /** ISO lower bound for the start date. */
  minStart?: string;
  required?: boolean;
  disabled?: boolean;
  /** When true, surface a Hijri preview under each input. */
  showHijri?: boolean;
}

export function DateRangePicker({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  maxEnd,
  minStart,
  required = false,
  disabled = false,
  showHijri,
}: DateRangePickerProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3">
      <DatePicker
        value={startValue}
        onChange={onStartChange}
        min={minStart}
        max={endValue ?? maxEnd}
        required={required}
        disabled={disabled}
        showHijri={showHijri}
        ariaLabel={t("entity_lookup.start_date", "Start date")}
        className="flex-1"
      />
      <div className="pt-2 text-text-tertiary">→</div>
      <DatePicker
        value={endValue}
        onChange={onEndChange}
        min={startValue ?? undefined}
        max={maxEnd}
        required={required}
        disabled={disabled}
        showHijri={showHijri}
        ariaLabel={t("entity_lookup.end_date", "End date")}
        className="flex-1"
      />
    </div>
  );
}
